import { ActivityLogFilterArea } from "#src/batteries-included-components/FilterAreas/ActivityLogFilterAreas/ActivityLogFilterAreas";
import { AssetActivityLogFilterArea } from "#src/batteries-included-components/FilterAreas/AssetActivityLogFilterAreas/AssetActivityLogFilterAreas";
import { RolePermissionsActivityLogFilterArea } from "#src/batteries-included-components/FilterAreas/RolePermissionsActivityLogFilterAreas.tsx";
import { ActivityLogsList } from "#src/batteries-included-components/Layouts/ActivityLogs/ActivityLogsList";
import { AccessDeniedLayout } from "#src/batteries-included-components/Layouts/Authorization/AccessDenied";
import { RolePermissionsActivityLogTablePanel } from "#src/batteries-included-components/Panels/TablePanels/RolePermissionsActivityLogTablePanel";
import { UserActivityLogTab } from "#src/batteries-included-components/Tabs/UserActivityLogTab";
import {
  useIsFeatureAvailable,
  useIsOpsHubExperience,
} from "#src/contexts/AuthenticatedContext.helpers";
import useLocalization from "#src/hooks/useLocalization";
import { useStorageKey } from "#src/hooks/useStorageKey";
import { useSearchParams } from "#src/Routers/hooks";
import { Page, Tab } from "@validereinc/common-components";
import {
  ActivityResource,
  type ActivityResourceType,
  type AssetActivityLogAssetType,
} from "@validereinc/domain";
import { toTitleCaseString } from "@validereinc/utilities";
import React from "react";
import { ACTIVITY_LOGS_BREADCRUMB } from ".";
import { AssetActivityLogTablePanel } from "./AssetActivityLogTablePanel";

export const ActivityLogsPage = () => {
  const [isOpsExperienceEnabled] = useIsOpsHubExperience();
  const { localize } = useLocalization();
  const [searchParams, setSearchParams] = useSearchParams();

  const [viewConfigStorageKey, tableStateStorageKey] = useStorageKey(
    ["view-configuration", "table-state"],
    "activity-log"
  );

  const [isChangeLogAvailable] = useIsFeatureAvailable({
    featureFlagQuery: "core:activity_logs",
    permissionQuery: { $and: ["activities:read", "reports:read"] },
  });
  const [isFacilitiesAvailable] = useIsFeatureAvailable({
    featureFlagQuery: "core:facilities",
    permissionQuery: "facilities:read",
  });
  const [isFlowsAvailable] = useIsFeatureAvailable({
    featureFlagQuery: "core:flows",
    permissionQuery: "flows:read",
  });
  const [isAssetGroupsAvailable] = useIsFeatureAvailable({
    featureFlagQuery: "core:networks",
    permissionQuery: "networks:read",
  });
  const [isEquipmentAvailable] = useIsFeatureAvailable({
    featureFlagQuery: "core:equipment",
    permissionQuery: "equipment:read",
  });
  const [isDevicesAvailable] = useIsFeatureAvailable({
    featureFlagQuery: "core:devices",
    permissionQuery: "devices:read",
  });
  const [isUserGroupsAvailable] = useIsFeatureAvailable({
    featureFlagQuery: "core:workflows",
    permissionQuery: "user_groups:read",
  });
  const [isRolesAvailable] = useIsFeatureAvailable({
    featureFlagQuery: "core:roles_permissions",
  });

  const assetActivityLogPageTabs = {
    AllResourcesTab: "all-resources",
    ...(isFacilitiesAvailable
      ? { FacilityTab: ActivityResource.FACILITY }
      : {}),
    ...(isFlowsAvailable ? { FlowTab: ActivityResource.FLOW } : {}),
    ...(isAssetGroupsAvailable
      ? { NetworkTab: ActivityResource.ASSET_GROUP }
      : {}),
    ...(isEquipmentAvailable
      ? { EquipmentTab: ActivityResource.EQUIPMENT }
      : {}),
    ...(isDevicesAvailable ? { DeviceTab: ActivityResource.DEVICE } : {}),
    ...(isUserGroupsAvailable ? { UserTab: "users" } : {}),
    ...(isRolesAvailable ? { RoleTab: ActivityResource.ROLE } : {}),
  } as const;
  const defaultTabKey = assetActivityLogPageTabs.AllResourcesTab;

  if (isOpsExperienceEnabled) {
    return <AccessDeniedLayout />;
  }

  return (
    <Page
      title="Change Log"
      breadcrumbs={[ACTIVITY_LOGS_BREADCRUMB]}
      activeTabKey={searchParams?.tab ?? defaultTabKey}
      onActiveTabKeyChange={(tabKey: string) => {
        setSearchParams({ tab: tabKey });
      }}
    >
      {isChangeLogAvailable
        ? Object.entries(assetActivityLogPageTabs).map(([key, value]) => {
            const viewConfigStorageKeyForTab = `${viewConfigStorageKey}-${value}`;
            const tableStateStorageKeyForTab = `${tableStateStorageKey}-${value}`;

            return (
              <Tab
                key={key}
                title={toTitleCaseString(localize(value), { intent: "ui" })}
                tabKey={value}
              >
                {value === assetActivityLogPageTabs.AllResourcesTab && (
                  <>
                    <ActivityLogFilterArea
                      viewConfigStorageKey={viewConfigStorageKeyForTab}
                    />
                    <ActivityLogsList
                      viewConfigStorageKey={viewConfigStorageKeyForTab}
                      tableStateStorageKey={tableStateStorageKeyForTab}
                    />
                  </>
                )}

                {(
                  [
                    assetActivityLogPageTabs.FacilityTab,
                    assetActivityLogPageTabs.FlowTab,
                    assetActivityLogPageTabs.NetworkTab,
                    assetActivityLogPageTabs.EquipmentTab,
                    assetActivityLogPageTabs.DeviceTab,
                  ] as ActivityResourceType[]
                ).includes(value as ActivityResourceType) && (
                  <>
                    <AssetActivityLogFilterArea
                      viewConfigStorageKey={viewConfigStorageKeyForTab}
                      assetType={value as AssetActivityLogAssetType}
                    />

                    <AssetActivityLogTablePanel
                      viewConfigStorageKey={viewConfigStorageKeyForTab}
                      tableStateStorageKey={tableStateStorageKeyForTab}
                      assetType={value as AssetActivityLogAssetType}
                    />
                  </>
                )}

                {value === assetActivityLogPageTabs.UserTab && (
                  <UserActivityLogTab
                    isUserGroupsAvailable={isUserGroupsAvailable}
                  />
                )}

                {value === assetActivityLogPageTabs.RoleTab && (
                  <>
                    <RolePermissionsActivityLogFilterArea
                      viewConfigStorageKey={viewConfigStorageKeyForTab}
                    />
                    <RolePermissionsActivityLogTablePanel
                      viewConfigStorageKey={viewConfigStorageKeyForTab}
                      tableStateStorageKey={tableStateStorageKeyForTab}
                    />
                  </>
                )}
              </Tab>
            );
          })
        : null}
    </Page>
  );
};
